// Lib
import { defer } from 'lodash';
import { useMemo } from 'react';
import Document from '@tiptap/extension-document';

// Utils
import { isTiptapEditorEmpty } from '../../../../common/tiptap/utils/isTiptapEditorEmpty';

// Hooks
import useClientCommonTiptapEditorExtensions, {
    UseClientCommonTiptapEditorExtensionsArgs,
} from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';

// Custom extensions
import { KeyboardShortcuts } from '../../../../common/tiptap/extensions/KeyboardShortcuts';
import { SubmitOnDone } from '../../../../common/tiptap/extensions/SubmitOnDone';

// Types
import { Editor } from '@tiptap/react';
import { Extensions, Command } from '@tiptap/core';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { DefaultTextMentionPluginKey } from '../../../../common/tiptap/extensions/mention/TextMention';

type UseCommentTiptapEditorExtensionsArgs = Omit<UseClientCommonTiptapEditorExtensionsArgs, 'placeholderOptions'> & {
    onSubmit?: (text: TiptapContent) => void;
    placeholder?: string;
};

/**
 * Gets the extensions for comment Tiptap editors.
 */
const useCommentTiptapEditorExtensions = ({
    placeholder,
    onEmptyBackspace,
    saveContent,
    suggestionsManager,
    isEditable,
    dispatch,
    onSubmit,
    onHighlightsChanged,
}: UseCommentTiptapEditorExtensionsArgs): Extensions => {
    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        onEmptyBackspace,
        placeholderOptions: { placeholder },
        suggestionsManager,
        isEditable,
        dispatch,
        onHighlightsChanged,
    });

    return useMemo(() => {
        const onReturn: Command = ({ editor }) => {
            if (isTiptapEditorEmpty(editor as unknown as Editor)) return false;

            // Don't submit the comment if the mention popup is active
            const mentionPluginState = DefaultTextMentionPluginKey.getState(editor.state);

            if (mentionPluginState.active) return false;

            // We need to defer the handling of this to prevent the Mousetrap
            //  event handler from being triggered - because the element would be
            //  editable and selected while the event gets propagated upwards
            defer(() => {
                const currentContent = editor.getJSON();
                onSubmit?.(currentContent as TiptapContent);
            });

            return !!onSubmit;
        };

        return [
            Document,
            ...commonExtensions,
            onSubmit && SubmitOnDone.configure({ onSubmit }),
            onSubmit &&
                KeyboardShortcuts.configure({
                    keyboardShortcuts: {
                        // Submit the caption on return or enter
                        Return: onReturn,
                        Enter: onReturn,
                    },
                }),
        ].filter(Boolean) as Extensions;
    }, [commonExtensions, suggestionsManager]);
};

export default useCommentTiptapEditorExtensions;
